import { Button, SelectField } from '@design-system';
import { getCurrencySymbolByCode } from '@utils/locations';
import { useTranslation } from 'next-i18next';
import React, { useContext, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import {
  availableUnits,
  COUNTRIES,
  COUNTRY_CODES,
  DEFAULT_COUNTRY_CODE,
} from '../../../constants/locations';
import { FooterLocaleVisibilityContext } from '../../../context/FooterLocaleVisibilityContext';
import { UserPreferences } from '../../../context/UserPreferencesContext';
import { useCountryCode } from '../../../hooks/useCountryCode';
import { useCurrency } from '../../../hooks/useCurrency';
import { useUnitOfMeasurement } from '../../../hooks/useUnitOfMeasurement';
import { TrackLocaleDrawerUpdated } from '../../../utils/analytics';

type Props = {
  form: UseFormReturn<Omit<UserPreferences, 'cookieConsent'>, object>;
  hideForm?: () => void;
  showTitle?: boolean;
};

export const LocaleForm: React.FC<Props> = ({
  form,
  hideForm,
  showTitle = true,
}) => {
  const { t } = useTranslation('footer');
  const { t: countryTranslation } = useTranslation('countries');
  const [currency] = useCurrency();
  const [unit, setUnit] = useUnitOfMeasurement();
  const [countryCode, setCountry] = useCountryCode();
  const { recentLaunchLocation } = useContext(FooterLocaleVisibilityContext);
  const [submitted, setSubmitted] = useState(false);

  const [working, setWorking] = useState(false);

  useEffect(
    () =>
      form.reset({
        countryCode,
        currency,
        unitOfMeasurement: unit,
      }),
    [currency, unit, countryCode],
  );

  const handleUpdate = form.handleSubmit(async (values) => {
    setWorking(true);

    try {
      const { countryCode, unitOfMeasurement } = values;

      const currencyCode =
        COUNTRIES[countryCode].currency ||
        COUNTRIES[DEFAULT_COUNTRY_CODE].currency;
      setCountry(countryCode);
      setUnit(unitOfMeasurement);

      TrackLocaleDrawerUpdated({
        source: recentLaunchLocation,
        countryCode,
        currencyCode,
        unitOfMeasurementCode: unitOfMeasurement,
      });
    } finally {
      setWorking(false);
      setSubmitted(true);
      hideForm && hideForm();
    }
  });

  useEffect(() => {
    if (submitted) {
      hideForm && hideForm();
    }
  }, [submitted]);

  return (
    <section
      className="flex flex-col text-neutral-6"
      data-testid="footer-drawer-section"
    >
      {showTitle && (
        <p className="mb-6 hidden text-lg font-bold md:block">
          {t('drawers.locale.title')}
        </p>
      )}

      <form className="space-y-4" onSubmit={handleUpdate}>
        <SelectField
          {...form.register('countryCode')}
          data-testid="field-countryCode"
          defaultValue={countryCode}
          label={t('drawers.locale.shippingLocationLabel')}
          options={COUNTRY_CODES.map((c) => {
            const currencyLabel = getCurrencySymbolByCode(
              COUNTRIES[c].currency,
            );

            return {
              key: `${countryTranslation(c).toString()} (${
                COUNTRIES[c].currency
              } ${currencyLabel})`,
              value: c,
            };
          })}
        />
        <section className="grid grid-cols-1 gap-3">
          <div>
            <SelectField
              {...form.register('unitOfMeasurement')}
              dataTestid="field-unitOfMeasurementCode"
              defaultValue={unit}
              label={t('drawers.locale.unitLabel')}
              options={availableUnits.map((u) => ({
                key: u.toLowerCase(),
                value: u,
              }))}
            />
          </div>
        </section>

        <div className="sticky bottom-0 !mt-12 bg-white pb-6">
          <div className="mx-auto w-full max-w-md">
            <Button
              fullWidth
              label={
                working
                  ? t('drawers.locale.buttonLoading')
                  : t('drawers.locale.button')
              }
              loading={working}
              onClick={handleUpdate}
              type="submit"
              variant="primary"
            />
          </div>
        </div>
      </form>
    </section>
  );
};
